import { useState, useCallback, useLayoutEffect } from 'react';

/**
 * @example const triggerFunction = useTriggerableLayoutEffect(cb);
 * @param cb The callback function that will be invoked after re-render by the useLayoutEffect.
 * @returns triggerFunction:: The function that will trigger the useLayoutEffect.
 */
const useTriggerableLayoutEffect = <F extends (...args: never[]) => void>(cb: F) => {
  const [state, setState] = useState<
    { triggered: false; args: null } | { triggered: true; args: Parameters<F> }
  >({
    triggered: false,
    args: null
  });

  useLayoutEffect(() => {
    if (!state.triggered) return;

    setState({
      triggered: false,
      args: null
    });

    cb(...state.args);
  }, [cb, state]);

  return useCallback((...args: Parameters<F>) => {
    setState({ triggered: true, args });
  }, []);
};

export default useTriggerableLayoutEffect;
